import React, { useState, useEffect } from 'react';
import AppNav from './AppNav';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  },
  {
    id: 'information-we-collect',
    label: 'Information We Collect',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
      </svg>
    ),
  },
  {
    id: 'how-we-use',
    label: 'How We Use Information',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    id: 'advertising-cookies',
    label: 'Advertising & Cookies',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
  },
  {
    id: 'third-party',
    label: 'Third-Party Services',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
      </svg>
    ),
  },
  {
    id: 'your-rights',
    label: 'Your Rights',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
  {
    id: 'data-security',
    label: 'Data Security',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
  },
  {
    id: 'contact',
    label: 'Contact Us',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
  },
];

export default function PrivacyPage({
  priceMode = 'student', onModeToggle, onNavigate,
  user = null, onUserChange,
}) {
  const isMobile = useIsMobile();
  const navH = isMobile ? 60 : 72;
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handler = () => {
      const offsets = SECTIONS.map(s => {
        const el = document.getElementById(s.id);
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: Math.abs(el.getBoundingClientRect().top - 130) };
      });
      const closest = offsets.sort((a, b) => a.top - b.top)[0];
      if (closest) setActiveSection(closest.id);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ── Sub-components matching Termspage style ── */
  const SectionHeader = ({ num, title, icon }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: 'linear-gradient(135deg, #0a2e14 0%, #1a4731 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#d4a017', flexShrink: 0,
        boxShadow: '0 4px 12px rgba(10,46,20,0.2)',
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#d4a017', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 2 }}>
          Section {num}
        </div>
        <h2 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#001805', margin: 0, letterSpacing: '-0.02em' }}>
          {title}
        </h2>
      </div>
    </div>
  );

  const Card = ({ children, accent = false }) => (
    <div style={{
      background: '#ffffff',
      border: `1px solid ${accent ? 'rgba(212,160,23,0.25)' : 'rgba(10,46,20,0.1)'}`,
      boxShadow: `6px 6px 0px 0px ${accent ? 'rgba(212,160,23,0.12)' : 'rgba(10,46,20,0.06)'}`,
      padding: isMobile ? '1.25rem' : '1.75rem',
      fontSize: 14, color: '#424841', lineHeight: 1.75,
      fontFamily: 'Manrope, sans-serif',
    }}>
      {children}
    </div>
  );

  const BulletList = ({ items }) => (
    <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: '#424841' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d4a017', marginTop: 7, flexShrink: 0, display: 'inline-block' }} />
          {item}
        </li>
      ))}
    </ul>
  );

  const InfoBox = ({ color = '#166534', bg = '#f0fdf4', border = 'rgba(22,101,52,0.2)', icon, children }) => (
    <div style={{ marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', background: bg, border: `1px solid ${border}` }}>
      <div style={{ color, flexShrink: 0, marginTop: 1 }}>{icon}</div>
      <p style={{ margin: 0, fontSize: 13, color, lineHeight: 1.55 }}>{children}</p>
    </div>
  );

  return (
    <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .privacy-section { scroll-margin-top: 100px; }
        .nav-link-privacy { transition: all 0.15s ease; }
        .nav-link-privacy:hover { background: #f0eded !important; }
      `}</style>

      <AppNav
        activePage="privacy"
        priceMode={priceMode}
        onModeToggle={onModeToggle}
        onNavigate={onNavigate}
        isMobile={isMobile}
        user={user}
        onUserChange={onUserChange}
      />
      <div style={{ height: navH }} />

      {/* ── Page Header ── */}
      <header style={{
        position: 'relative', overflow: 'hidden',
        padding: isMobile ? '3rem 1.25rem 3.5rem' : '4.5rem 2rem 5rem',
        marginBottom: isMobile ? '2rem' : '3rem',
        animation: 'fadeUp 0.5s ease both',
      }}>
        {/* Background */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1400&q=80)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          zIndex: 0,
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(0,24,5,0.88) 0%, rgba(10,46,20,0.80) 60%, rgba(0,24,5,0.85) 100%)',
          zIndex: 1,
        }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(to right, #d4a017 0%, rgba(212,160,23,0.15) 50%, transparent 100%)', zIndex: 2 }} />

        <div style={{ position: 'relative', zIndex: 3, maxWidth: 1280, margin: '0 auto' }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.35)',
            padding: '5px 12px', marginBottom: 18,
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'Space Grotesk, sans-serif' }}>Privacy &amp; Data Protection</span>
          </div>

          <h1 style={{
            fontFamily: 'Epilogue, sans-serif',
            fontSize: isMobile ? 'clamp(1.75rem, 8vw, 2.5rem)' : 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', lineHeight: 1.05,
            marginBottom: 16,
          }}>
            Privacy<br />
            <span style={{ color: '#d4a017' }}>Policy</span>
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, maxWidth: 600, marginBottom: 0, fontFamily: 'Manrope, sans-serif' }}>
            Last updated: <strong style={{ color: '#d4a017' }}>May 9, 2026.</strong> This Privacy Policy explains how NaijaAgro Predict collects, uses, and protects your information when you use our platform, including how we use advertising cookies through Google AdSense.
          </p>
        </div>
      </header>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '0 1rem 3rem' : '0 2rem 5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: isMobile ? '1.5rem' : '3rem', alignItems: 'start' }}>

          {/* ── Sticky Sidebar ── */}
          {!isMobile && (
            <aside style={{ position: 'sticky', top: navH + 20 }}>
              <div style={{ marginBottom: 8, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#727970', fontFamily: 'Space Grotesk, sans-serif', padding: '0 12px' }}>
                Quick Navigation
              </div>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {SECTIONS.map(s => {
                  const isActive = activeSection === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className="nav-link-privacy"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px',
                        background: isActive ? '#f0eded' : 'transparent',
                        border: 'none',
                        borderLeft: `3px solid ${isActive ? '#d4a017' : 'transparent'}`,
                        cursor: 'pointer', textAlign: 'left',
                        fontSize: 13, fontWeight: isActive ? 700 : 500,
                        color: isActive ? '#001805' : '#424841',
                        fontFamily: 'Manrope, sans-serif',
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ color: isActive ? '#d4a017' : '#c2c8bf', flexShrink: 0 }}>{s.icon}</span>
                      {s.label}
                    </button>
                  );
                })}
              </nav>

              {/* GDPR/NDPR note */}
              <div style={{
                marginTop: 20,
                background: '#0a2e14', color: '#fff',
                padding: '1.25rem', boxShadow: '6px 6px 0px 0px rgba(212,160,23,0.15)',
              }}>
                <div style={{ color: '#d4a017', marginBottom: 8 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
                <p style={{ fontFamily: 'Epilogue, sans-serif', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>NDPR Compliant</p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, marginBottom: 0 }}>
                  This policy is drafted in alignment with Nigeria's Data Protection Regulation (NDPR) and international best practices.
                </p>
              </div>
            </aside>
          )}

          {/* ── Main Content ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '2rem' : '2.5rem' }}>

            {/* 1. Overview */}
            <section id="overview" className="privacy-section">
              <SectionHeader num="1" title="Overview" icon={SECTIONS[0].icon} />
              <Card>
                <p style={{ marginTop: 0 }}>NaijaAgro Predict ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy describes what information we collect when you visit <strong>naijaagropredict.web.app</strong> (the "Platform"), how we use it, and your rights in relation to it.</p>
                <p>By accessing or using the Platform, you agree to the practices described in this Policy. If you do not agree, please discontinue use of the Platform.</p>
                <InfoBox
                  color="#166534" bg="#f0fdf4" border="rgba(22,101,52,0.2)"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
                >
                  This policy applies to all visitors and users of NaijaAgro Predict, including users who access the Platform on mobile devices or through third-party integrations.
                </InfoBox>
              </Card>
            </section>

            {/* 2. Information We Collect */}
            <section id="information-we-collect" className="privacy-section">
              <SectionHeader num="2" title="Information We Collect" icon={SECTIONS[1].icon} />
              <Card>
                <p style={{ marginTop: 0 }}>We collect two categories of information:</p>

                <div style={{ marginTop: 18, marginBottom: 18 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#001805', fontFamily: 'Epilogue, sans-serif', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 4, height: 16, background: '#d4a017', display: 'inline-block', borderRadius: 2 }} />
                    A. Information You Provide
                  </div>
                  <BulletList items={[
                    'Commodity selections and prediction inputs you enter into the price prediction tool.',
                    'State or region preferences you select when filtering market data.',
                    'Contact information (name, email address) if you contact us directly.',
                  ]} />
                </div>

                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#001805', fontFamily: 'Epilogue, sans-serif', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 4, height: 16, background: '#d4a017', display: 'inline-block', borderRadius: 2 }} />
                    B. Information Collected Automatically
                  </div>
                  <BulletList items={[
                    'Log data: IP address, browser type, operating system, referring URL, pages visited, and time spent on the Platform.',
                    'Device information: screen size, device type (mobile, desktop), and browser version.',
                    'Cookies and similar tracking technologies placed by us and by our advertising partner, Google AdSense.',
                    'Usage patterns: which commodities you view, which tools you interact with, and how you navigate the Platform.',
                  ]} />
                </div>
              </Card>
            </section>

            {/* 3. How We Use Information */}
            <section id="how-we-use" className="privacy-section">
              <SectionHeader num="3" title="How We Use Your Information" icon={SECTIONS[2].icon} />
              <Card>
                <p style={{ marginTop: 0 }}>We use the information we collect for the following purposes:</p>
                <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  {[
                    { title: 'Platform Operation', desc: 'To deliver price predictions, display market data, and provide the core features of the Platform.' },
                    { title: 'Model Improvement', desc: 'To refine our XGBoost predictive models and improve the accuracy of future price estimates.' },
                    { title: 'Analytics', desc: 'To understand how users interact with the Platform so we can improve usability and content.' },
                    { title: 'Advertising', desc: 'To serve relevant, personalised advertisements through Google AdSense using cookie-based targeting.' },
                    { title: 'Communication', desc: 'To respond to enquiries or feedback submitted through our contact channels.' },
                    { title: 'Legal Compliance', desc: 'To comply with applicable Nigerian laws and to protect our legal rights where necessary.' },
                  ].map((item, i) => (
                    <div key={i} style={{ padding: '14px 16px', background: '#f6f3f2', border: '1px solid rgba(10,46,20,0.08)' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#001805', marginBottom: 6 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: '#424841', lineHeight: 1.55 }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: 16, fontSize: 13, color: '#424841' }}>We do <strong>not</strong> sell your personal information to third parties for their own marketing purposes.</p>
              </Card>
            </section>

            {/* 4. Advertising & Cookies */}
            <section id="advertising-cookies" className="privacy-section">
              <SectionHeader num="4" title="Advertising & Cookies" icon={SECTIONS[3].icon} />
              <Card>
                <p style={{ marginTop: 0 }}>
                  NaijaAgro Predict uses <strong>Google AdSense</strong>, a third-party advertising service operated by Google LLC, to display advertisements on the Platform. This section explains how Google AdSense uses cookies and what choices you have.
                </p>

                <div style={{ marginTop: 20, marginBottom: 16, padding: '16px 20px', background: 'rgba(10,46,20,0.04)', borderLeft: '4px solid #d4a017' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#001805', fontFamily: 'Epilogue, sans-serif', marginBottom: 8 }}>What is Google AdSense?</div>
                  <p style={{ margin: 0, fontSize: 13, color: '#424841', lineHeight: 1.65 }}>
                    Google AdSense is an advertising programme that allows website publishers to earn revenue by displaying ads. Google uses cookies — including the <strong>DoubleClick cookie</strong> — to serve ads based on your prior visits to this website and other websites on the internet.
                  </p>
                </div>

                <div style={{ marginTop: 16, marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#001805', fontFamily: 'Epilogue, sans-serif', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 4, height: 16, background: '#d4a017', display: 'inline-block', borderRadius: 2 }} />
                  What Cookies Are Used
                </div>
                <BulletList items={[
                  'Essential cookies: required for the Platform to function correctly (session management, navigation).',
                  'Analytics cookies: used to measure traffic and understand how users interact with the Platform.',
                  'Advertising cookies (Google AdSense / DoubleClick): used to serve personalised advertisements based on your browsing history and interests across websites.',
                  'Preference cookies: used to remember your settings such as selected state or commodity filters.',
                ]} />

                <div style={{ marginTop: 20, marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#001805', fontFamily: 'Epilogue, sans-serif', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 4, height: 16, background: '#d4a017', display: 'inline-block', borderRadius: 2 }} />
                  Your Choices
                </div>
                <p style={{ fontSize: 13, color: '#424841', lineHeight: 1.65 }}>
                  You can opt out of personalised advertising by Google at any time. You may also manage your cookie preferences through your browser settings. Note that disabling cookies may affect the functionality of certain parts of the Platform.
                </p>

                {/* External links */}
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    {
                      href: 'https://adssettings.google.com',
                      label: 'Google Ad Settings',
                      desc: 'Opt out of personalised ads from Google',
                    },
                    {
                      href: 'https://policies.google.com/technologies/ads',
                      label: 'Google Advertising Policies',
                      desc: 'How Google uses data when you use its partners\' sites',
                    },
                    {
                      href: 'https://policies.google.com/privacy',
                      label: 'Google Privacy Policy',
                      desc: 'Full details on Google\'s data practices',
                    },
                  ].map((link, i) => (
                    <a
                      key={i}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        padding: '12px 16px', background: '#ffffff',
                        border: '1px solid rgba(10,46,20,0.12)',
                        boxShadow: '4px 4px 0px 0px rgba(10,46,20,0.06)',
                        textDecoration: 'none',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '6px 6px 0px 0px rgba(212,160,23,0.15)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '4px 4px 0px 0px rgba(10,46,20,0.06)'; }}
                    >
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: '#0a2e14', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#001805', fontFamily: 'Epilogue, sans-serif' }}>{link.label}</div>
                        <div style={{ fontSize: 11, color: '#727970' }}>{link.desc}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </Card>
            </section>

            {/* 5. Third-Party Services */}
            <section id="third-party" className="privacy-section">
              <SectionHeader num="5" title="Third-Party Services" icon={SECTIONS[4].icon} />
              <Card>
                <p style={{ marginTop: 0 }}>The Platform integrates with several third-party services. Each of these services operates under its own privacy policy, which we encourage you to review.</p>
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    {
                      name: 'WFP VAM API',
                      role: 'Provides real-time and historical agricultural commodity price data for Nigeria.',
                      href: 'https://dataviz.vam.wfp.org',
                    },
                    {
                      name: 'Google AdSense',
                      role: 'Serves advertisements on the Platform using cookie-based targeting.',
                      href: 'https://policies.google.com/privacy',
                    },
                    {
                      name: 'Newsdata.io',
                      role: 'Provides live Nigerian news article feeds displayed on the News page.',
                      href: 'https://newsdata.io/privacy-policy',
                    },
                    {
                      name: 'Unsplash / Freepik',
                      role: 'Provides licensed commodity photography displayed across the Platform.',
                      href: 'https://unsplash.com/privacy',
                    },
                  ].map((svc, i) => (
                    <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 16px', background: '#f6f3f2', border: '1px solid rgba(10,46,20,0.08)', alignItems: 'flex-start' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#d4a017', marginTop: 6, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#001805', marginBottom: 3 }}>{svc.name}</div>
                        <div style={{ fontSize: 12, color: '#424841', lineHeight: 1.55 }}>{svc.role}</div>
                      </div>
                      <a href={svc.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#0a2e14', fontWeight: 700, whiteSpace: 'nowrap', textDecoration: 'none', flexShrink: 0 }}>Privacy Policy ↗</a>
                    </div>
                  ))}
                </div>
                <InfoBox
                  color="#92400e" bg="#fffbeb" border="rgba(146,64,14,0.2)"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                >
                  We are not responsible for the data practices of third-party services. Links to third-party sites are provided for convenience only and do not imply endorsement.
                </InfoBox>
              </Card>
            </section>

            {/* 6. Your Rights */}
            <section id="your-rights" className="privacy-section">
              <SectionHeader num="6" title="Your Rights" icon={SECTIONS[5].icon} />
              <Card>
                <p style={{ marginTop: 0 }}>Under applicable Nigerian data protection law (NDPR) and international privacy standards, you have the following rights regarding your personal data:</p>
                <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  {[
                    { title: 'Right to Access', desc: 'You may request a copy of any personal data we hold about you.' },
                    { title: 'Right to Rectification', desc: 'You may ask us to correct inaccurate or incomplete personal data.' },
                    { title: 'Right to Erasure', desc: 'You may request deletion of your personal data where we have no legitimate reason to continue holding it.' },
                    { title: 'Right to Restrict Processing', desc: 'You may ask us to limit how we use your data in certain circumstances.' },
                    { title: 'Right to Object', desc: 'You may object to our processing of your data, including for direct marketing or advertising purposes.' },
                    { title: 'Right to Opt Out of Personalised Ads', desc: 'You may opt out of Google AdSense personalised advertising at any time via Google Ad Settings.' },
                  ].map((right, i) => (
                    <div key={i} style={{ padding: '14px 16px', background: '#f6f3f2', border: '1px solid rgba(10,46,20,0.08)' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#001805', marginBottom: 6 }}>{right.title}</div>
                      <div style={{ fontSize: 12, color: '#424841', lineHeight: 1.55 }}>{right.desc}</div>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: 16, fontSize: 13, color: '#424841' }}>
                  To exercise any of these rights, contact us at <a href="mailto:naijaagropredict@gmail.com" style={{ color: '#0a2e14', fontWeight: 700 }}>naijaagropredict@gmail.com</a>. We will respond within 30 days of receiving your request.
                </p>
              </Card>
            </section>

            {/* 7. Data Security */}
            <section id="data-security" className="privacy-section">
              <SectionHeader num="7" title="Data Security" icon={SECTIONS[6].icon} />
              <Card>
                <p style={{ marginTop: 0 }}>We implement reasonable technical and organisational measures to protect your information against unauthorised access, loss, alteration, or disclosure. These measures include:</p>
                <BulletList items={[
                  'HTTPS encryption for all data transmitted between your browser and the Platform.',
                  'Restricted internal access to any personally identifiable information we hold.',
                  'Regular review of data handling practices to identify and address potential vulnerabilities.',
                ]} />
                <InfoBox
                  color="#92400e" bg="#fffbeb" border="rgba(146,64,14,0.2)"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                >
                  No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security. By using the Platform, you acknowledge this inherent risk.
                </InfoBox>
                <p style={{ marginTop: 16, fontSize: 13, color: '#424841' }}>
                  We retain personal data only for as long as necessary to fulfil the purposes set out in this Policy, or as required by applicable law. Data submitted through the prediction tool is anonymised and aggregated for model training purposes.
                </p>
              </Card>
            </section>

            {/* 8. Contact */}
            <section id="contact" className="privacy-section">
              <SectionHeader num="8" title="Contact Us" icon={SECTIONS[7].icon} />
              <Card accent>
                <p style={{ marginBottom: 20, marginTop: 0 }}>
                  If you have any questions about this Privacy Policy, wish to exercise your data rights, or want to report a concern about how your information is handled, please contact us:
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                  <a href="mailto:naijaagropredict@gmail.com" style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', background: '#0a2e14', textDecoration: 'none',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    boxShadow: '4px 4px 0px 0px rgba(212,160,23,0.2)',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '6px 6px 0px 0px rgba(212,160,23,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '4px 4px 0px 0px rgba(212,160,23,0.2)'; }}
                  >
                    <div style={{ color: '#d4a017', flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 3 }}>Email</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#d4a017' }}>naijaagropredict@gmail.com</div>
                    </div>
                  </a>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', background: '#f6f3f2',
                    border: '1px solid rgba(10,46,20,0.12)',
                    boxShadow: '4px 4px 0px 0px rgba(10,46,20,0.06)',
                  }}>
                    <div style={{ color: '#0a2e14', flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.13 6.13l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#727970', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 3 }}>Phone</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#001805' }}>08069305651 · 07087917444</div>
                    </div>
                  </div>
                </div>

                {/* Policy change notice */}
                <div style={{ marginTop: 20, padding: '12px 16px', background: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9a7010" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  <p style={{ margin: 0, fontSize: 12, color: '#9a7010', lineHeight: 1.55 }}>
                    We may update this Privacy Policy periodically. When changes are made, the "Last updated" date at the top of this page will be revised. Your continued use of the Platform after any changes constitutes acceptance of the revised policy.
                  </p>
                </div>
              </Card>
            </section>

          </div>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer style={{
        background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)',
        color: '#fff', padding: '1.75rem 2rem',
        borderTop: '3px solid #d4a017',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 12 }}>
          <div style={{ fontFamily: 'Epilogue, sans-serif', fontWeight: 800, fontSize: 15, color: '#d4a017' }}>NaijaAgro Predict</div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Home', nav: 'home' },
              { label: 'About Us', nav: 'about' },
              { label: 'Terms', nav: 'terms' },
              { label: 'Privacy Policy', nav: 'privacy' },
              { label: 'Contact', nav: 'about' },
            ].map(l => (
              <button key={l.label} onClick={() => onNavigate && onNavigate(l.nav)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, color: 'rgba(255,255,255,0.6)',
                fontFamily: 'Manrope, sans-serif', padding: 0,
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#d4a017'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
              >{l.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>© 2026 NaijaAgro Predict. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}